import { useId, useState, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { PlusIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { Label } from 'src/components/ui/label';
import {
  SelectDropdown,
  type SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import { useNamespaces } from 'src/api/rpc/hooks/namespaces/useNamespaces';
import { useCan } from 'src/hooks/useCan';
import { CreateNamespaceDialog } from './CreateNamespaceDialog';
import {
  formatNamespaceLabel,
  type NamespaceNoneOption,
} from './namespaceLabels';
import {
  isNamespaceListReady,
  selectedNamespacePickerOption,
} from './namespacePicker';

export type { NamespaceNoneOption };

function NamespacePickerControls({
  fieldId,
  options,
  selected,
  noneOptionId,
  onChange,
  placeholder,
  searchPlaceholder,
  disabled,
  allowCreate,
  canCreate,
  compact,
}: {
  fieldId: string;
  options: SelectDropdownOption[];
  selected: SelectDropdownOption;
  noneOptionId: string;
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  disabled?: boolean;
  allowCreate: boolean;
  canCreate: boolean;
  compact: boolean;
}) {
  const [creating, setCreating] = useState(false);
  const offerCreate = !disabled && allowCreate && canCreate;
  return (
    <div className={compact ? 'min-w-40' : 'min-w-56'}>
      <SelectDropdown
        id={fieldId}
        options={options}
        value={selected}
        onChange={(option) => onChange(option?.id ?? noneOptionId)}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        fullWidth
        disabled={disabled}
        footerAction={
          offerCreate
            ? {
                icon: <PlusIcon className="mr-2 size-4" />,
                label: (
                  <T context="namespace picker action, create a new namespace">
                    New namespace
                  </T>
                ),
                onSelect: () => setCreating(true),
              }
            : undefined
        }
      />
      {offerCreate && (
        <CreateNamespaceDialog
          open={creating}
          onOpenChange={setCreating}
          onCreated={onChange}
        />
      )}
    </div>
  );
}

export function NamespaceField({
  value,
  onChange,
  disabled,
  allowCreate = true,
  noneOption,
  label,
  description,
  compact = false,
}: {
  /** Selected namespace id, or the none-option id. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  allowCreate?: boolean;
  noneOption: NamespaceNoneOption;
  label?: ReactNode;
  description?: ReactNode;
  /** Drop the description and keep the control on one row — table cells. */
  compact?: boolean;
}) {
  const gt = useGT();
  const fieldId = useId();
  const { data: namespaces, isError } = useNamespaces();
  const [canCreateNamespace] = useCan(Action.Create, Subject.Namespace);
  const listReady = isNamespaceListReady(namespaces);

  const options: SelectDropdownOption[] = [
    noneOption,
    ...(namespaces ?? []).map((namespace) => ({
      id: namespace.id,
      label: formatNamespaceLabel(gt, namespace),
      display: namespace.name,
    })),
  ];
  const selected = selectedNamespacePickerOption(
    options,
    value,
    noneOption.id,
    listReady,
    isError
      ? gt('Unavailable', {
          context:
            'namespace picker display when the user cannot read the namespace list',
        })
      : gt('Loading…', {
          context: 'namespace picker display while the list is loading',
        }),
  );
  const picker = (
    <NamespacePickerControls
      fieldId={fieldId}
      options={options}
      selected={selected}
      noneOptionId={noneOption.id}
      onChange={onChange}
      placeholder={gt('Search namespaces…', {
        context: 'placeholder for a namespace picker',
      })}
      searchPlaceholder={gt('Search…', {
        context: 'search box in a namespace picker',
      })}
      disabled={disabled || !listReady}
      allowCreate={allowCreate}
      canCreate={canCreateNamespace}
      compact={compact}
    />
  );

  if (compact) return picker;

  return (
    <div className="flex flex-col gap-1.5">
      {label ? <Label htmlFor={fieldId}>{label}</Label> : null}
      {description ? (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      ) : null}
      {picker}
    </div>
  );
}
